import AuthService from './authService';
import ContextService from './contextService';
import Utils from './utils';
import { IFeature } from '@esri/arcgis-rest-types';
import { applyEdits } from '@esri/arcgis-rest-feature-layer';
import ConfigService from './configService';
import { IApplyEditsResult, IEditFeatureResult, IRelatedRecordGroup, IQueryRelatedResponse, queryFeatures, queryRelated, getLayer, ILayerDefinition } from '@esri/arcgis-rest-feature-layer';
import { ISocioEconomicImpactsTab, IPrivateSectorSocioEconomicImpactsTab, IPrivateSectorSplitUpValues, IReportCountriesIndicators, IPrivateSectorResultsAndBenefitsItem, ISuccessFactorsPolicyStrategies, IPrivateSectorSuccessFactorsPlanningMethodology, IPrivateSectorSuccessFactorsFinancialFlow, IPrivateSectorSuccessFactorsMonitoringSystem } from '../components/reports/EditReport/models/IEditReportState';

export default class QueryService {

    /**
     * Get success results from an applyedit query
     * @param res 
     * @returns 
     */
    private static getSuccessResults(res: IEditFeatureResult[]): any[] {
        return res.filter((item: IEditFeatureResult) => item.success === true)
    }

    /**
     * Get success results from an applyedit query
     * @param res 
     * @returns 
     */
    private static checkSuccessUpdate(res: IApplyEditsResult): boolean {
        let isOK = true;

        // 1) Adds
        if (res.addResults && res.addResults.length > 0) {
            isOK = res.addResults.filter((item: IEditFeatureResult) => item.success === false).length === 0;
        }

        // 1) updates
        if (res.updateResults && res.updateResults.length > 0) {
            isOK = isOK && res.updateResults.filter((item: IEditFeatureResult) => item.success === false).length === 0;
        }

        return isOK;
    }

    /**
     * Query ESRI layer
     * @param url 
     * @param where 
     * @param returnGeometry 
     * @param outFields
     * @param orderByFields
     * @param geometry
     * @param useAuth
     * @returns 
     */
    public static async queryFeatures(url: string, where: string = '1=1', returnGeometry: boolean = false, outFields: '*' | string[] | undefined = '*', orderByFields: string = '', geometry: any = null, useAuth = true): Promise<IFeature[]> {

        if (!url) {
            return Promise.reject('Invalid url');
        }

        return new Promise<any>(async (resolve: Function, reject: Function) => {
            try {
                const res: any = await queryFeatures({
                    url: url,
                    authentication: useAuth ? AuthService.getInstance().session : undefined,
                    where: where,
                    outFields: outFields,
                    orderByFields: orderByFields,
                    returnGeometry: returnGeometry,
                    geometry: geometry
                });

                resolve(res.features as IFeature[]);
            }
            catch (err: any) {
                reject(err);
            };
        });
    }

    /**
     * Query ESRI layer
     * @param url 
     * @param where 
     * @param returnGeometry 
     * @param outFields
     * @param orderByFields
     * @param geometry
     * @param useAuth 
     * @returns 
     */
    public static async queryFeaturesByObjectids(url: string, objectIds: number[], returnGeometry: boolean = false, outFields: '*' | string[] | undefined = '*', orderByFields: string = '', useAuth = true): Promise<IFeature[]> {

        if (!url) {
            return Promise.reject('Invalid url');
        }

        return new Promise<any>(async (resolve: Function, reject: Function) => {
            try {
                const res: any = await queryFeatures({
                    url: url,
                    authentication: useAuth ? AuthService.getInstance().session : undefined,
                    objectIds: objectIds,
                    outFields: outFields,
                    orderByFields: orderByFields,
                    returnGeometry: returnGeometry
                });

                resolve(res.features as IFeature[]);
            }
            catch (err: any) {
                reject(err);
            };
        });
    }

    /**
     * Query ESRI layer
     * @param url 
     * @param where 
     * @param returnGeometry 
     * @param outFields
     * @param orderByFields
     * @param geometry 
     * @returns 
     */
    public static async getLayerFieldsInfos(layerIndex: number): Promise<ILayerDefinition> {
        if (ContextService.getInstance().layersInfos[layerIndex]) {
            return Promise.resolve(ContextService.getInstance().layersInfos[layerIndex]);
        } else {
            const url: string = `${ConfigService.getConfig().agol.operationalLayers.baseURL}/${layerIndex}`;

            return new Promise<any>(async (resolve: Function, reject: Function) => {
                try {
                    const res: ILayerDefinition = await getLayer({
                        url: url,
                        authentication: AuthService.getInstance().session
                    });

                    ContextService.getInstance().layersInfos[layerIndex] = res;

                    resolve(res);
                }
                catch (err: any) {
                    reject(err);
                };
            });
        }
    }

    /**
     * Query related features
     * @param url 
     * @param relationshipId 
     * @param objectIds 
     * @param returnGeometry 
     * @param outFields 
     * @param orderByFields
     * @param useAuth
     * @returns 
     */
    public static async queryRelatedFeatures(url: string, relationshipId: number, objectIds: number[], returnGeometry: boolean = false, outFields: '*' | string[] | undefined = '*', orderByFields: string = '', useAuth = true): Promise<IRelatedRecordGroup[]> {

        if (!url) {
            return Promise.reject('Invalid url');
        }

        return new Promise<IRelatedRecordGroup[]>(async (resolve: Function, reject: Function) => {
            try {
                const res: IQueryRelatedResponse = await queryRelated({
                    url: url,
                    authentication: useAuth ? AuthService.getInstance().session : undefined,
                    objectIds: objectIds,
                    relationshipId: relationshipId,
                    httpMethod: 'POST',
                    params: {
                        outFields: outFields,
                        orderByFields: orderByFields,
                        returnGeometry: returnGeometry
                    }
                });

                if (res && res.relatedRecordGroups) {
                    resolve(res.relatedRecordGroups)
                } else {
                    resolve([]);
                }
            }
            catch (err: any) {
                reject(err);
            };
        });
    }

    /**
     * Query GADM layer
     * @param layerIndex 
     * @param orderBy 
     * @param whereField1 
     * @param whereValue1 
     * @param whereField2 
     * @param whereValue2 
     * @param returnGeometry
     * @returns 
     */
    public static async queryGADMFeatures(layerIndex: number, orderBy: string, whereField1: string = '', whereValue1: string = '', whereField2: string = '', whereValue2: string = '', returnGeometry: boolean = false): Promise<IFeature[]> {
        let aggr1: string = whereValue1 ? whereValue1 : '';
        let aggr2: string = whereValue2 ? `${whereValue1}_${whereValue2}` : '';

        try {
            if (layerIndex === 0 && ContextService.getInstance().gadm_0Features.length) {
                return Promise.resolve(ContextService.getInstance().gadm_0Features);
            } else if (layerIndex === 1 && ContextService.getInstance().gadm_1Features[aggr1]) {
                return Promise.resolve(ContextService.getInstance().gadm_1Features[aggr1]);
            } else if (layerIndex === 2 && ContextService.getInstance().gadm_2Features[aggr2]) {
                return Promise.resolve(ContextService.getInstance().gadm_2Features[aggr2]);
            } else {
                let where: string = '1=1';

                if (layerIndex === 1) {
                    where = `${whereField1} = '${whereValue1}'`;
                } else if (layerIndex === 2) {
                    where = `${whereField1} = '${whereValue1}' AND ${whereField2} = '${whereValue2}'`;
                }

                const res: IFeature[] = await QueryService.queryFeatures(Utils.getGADMLayerQuery(layerIndex), where, returnGeometry, '*', orderBy, null, false)

                if (layerIndex === 0) {
                    ContextService.getInstance().gadm_0Features = res;
                } else if (layerIndex === 1) {
                    ContextService.getInstance().gadm_1Features[aggr1] = res;
                } else if (layerIndex === 2) {
                    ContextService.getInstance().gadm_2Features[aggr2] = res;
                }

                return Promise.resolve(res);
            }
        }
        catch (err: any) {
            return Promise.reject(err);
        };
    }


    /**
     * Query reference table
     * @param layerIndex
     * @returns 
     */
    public static async queryReferenceFeatures(layerIndex: number): Promise<IFeature[]> {

        try {
            if (!ContextService.getInstance().refFeatures[layerIndex]) {
                ContextService.getInstance().refFeatures[layerIndex] = await QueryService.queryFeatures(Utils.getReferenceTableQuery(layerIndex));
            }

            return ContextService.getInstance().refFeatures[layerIndex];
        }
        catch (err: any) {
            return Promise.reject(err);
        };
    }

    /**
     * Query reference table
     * @param layerIndex
     * @returns 
     */

    /**
     * Query ESRI layer
     * @param url 
     * @param where 
     * @param returnGeometry 
     * @param outFields
     * @param orderByFields
     * @param geometry
     * @param useAuth
     * @returns 
     */
    public static async queryFilterReferenceFeatures(url: string, where: string = '1=1', returnGeometry: boolean = false, outFields: '*' | string[] | undefined = '*', orderByFields: string = '', geometry: any = null, useAuth = true): Promise<IFeature[]> {

        if (!url) {
            return Promise.reject('Invalid url');
        }

        return new Promise<any>(async (resolve: Function, reject: Function) => {
            try {
                const res: any = await queryFeatures({
                    url: url,
                    authentication: useAuth ? AuthService.getInstance().session : undefined,
                    where: where,
                    outFields: outFields,
                    orderByFields: orderByFields,
                    returnGeometry: returnGeometry,
                    geometry: geometry
                });

                resolve(res.features as IFeature[]);
            }
            catch (err: any) {
                reject(err);
            };
        });
    }

    /**
      * Apply edits.
      * @param index
      * @param adds
      * @param updates
      * @param deletes
      * @returns
      */
    public static async applyEdits(index: number, adds: IFeature[] = [], updates: IFeature[] = [], deletes: number[] = []): Promise<any> {
        if (!adds.length && !updates.length && !deletes.length) {
            return Promise.resolve({ adds: [], updates: [], deletes: [] });
        }
        return new Promise<any>(async (resolve: Function, reject: Function) => {

            try {
                const url: string = `${ConfigService.getConfig().agol.operationalLayers.baseURL}/${index}`;
                const res: IApplyEditsResult = await applyEdits({
                    url: url,
                    authentication: AuthService.getInstance().session,
                    hideToken: true,
                    adds: adds,
                    updates: updates,
                    deletes: deletes,
                    rollbackOnFailure: true
                });

                if (QueryService.checkSuccessUpdate(res)) {
                    resolve({ adds: res.addResults, updates: res.updateResults, deletes: res.deleteResults });
                } else {
                    reject();
                }
            }
            catch (err: any) {
                reject(err);
            };
        });
    }

    /**
     * Save edits
     * @param metadataModality
     * @param successFactors
     * @returns 
     */
    public static async saveEdits(
        metadataModality: { adds?: any[], updates?: any[] } = {},
        successFactors: {
            policies?: { adds?: any[], deletes?: any[] },
            financialFlows?: { adds?: any[], updates?: any[] },
            planningMethodologies?: { adds?: any[], deletes?: any[] },
            monitoringSystems?: { adds?: any[], deletes?: any[] },
        } = {},
        resultsAndBenefits: { adds?: any[], updates?: any[], deletes?: any[], addsJobs?: ISocioEconomicImpactsTab[], updatesJobs?: ISocioEconomicImpactsTab[], deletesJobs?: number[] } = {}
    ): Promise<any> {
        try {
            // 1) Report main
            const reportMain = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMain.index, [], metadataModality.updates);

            // 2) Success factors
            if (successFactors) {
                // 2.1) Policies
                if (successFactors.policies) {
                    const policies = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                        successFactors.policies && successFactors.policies.adds ? successFactors.policies.adds : [],
                        [],
                        successFactors.policies && successFactors.policies.deletes ? successFactors.policies.deletes : []
                    );
                }

                // 2.2) Financial flows
                if (successFactors.financialFlows) {
                    const financialFlows = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                        successFactors.financialFlows && successFactors.financialFlows.adds ? successFactors.financialFlows.adds : [],
                        successFactors.financialFlows && successFactors.financialFlows.updates ? successFactors.financialFlows.updates : [],
                        []
                    );
                }

                // 2.3) Technical planning
                if (successFactors.planningMethodologies) {
                    const methodologies = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                        successFactors.planningMethodologies && successFactors.planningMethodologies.adds ? successFactors.planningMethodologies.adds : [],
                        [],
                        successFactors.planningMethodologies && successFactors.planningMethodologies.deletes ? successFactors.planningMethodologies.deletes : []
                    );
                }

                // 2.4) Technical planning
                if (successFactors.monitoringSystems) {
                    const systems = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                        successFactors.monitoringSystems && successFactors.monitoringSystems.adds ? successFactors.monitoringSystems.adds : [],
                        [],
                        successFactors.monitoringSystems && successFactors.monitoringSystems.deletes ? successFactors.monitoringSystems.deletes : []
                    );
                }

                // 3) Results and benefits / Jobs
                if (resultsAndBenefits) {

                    // 3.1) Results and benefits
                    const rbs = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRb.index,
                        resultsAndBenefits.adds || [],
                        resultsAndBenefits.updates || [],
                        resultsAndBenefits.deletes || [],
                    );

                    // 3.2) Jobs
                    if (resultsAndBenefits) {
                        const jobsItems: ISocioEconomicImpactsTab[] = resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs?.length ? resultsAndBenefits.addsJobs :
                            resultsAndBenefits.updatesJobs && resultsAndBenefits.updatesJobs?.length ? resultsAndBenefits.updatesJobs : [];
                        const jobs: any[] = [];

                        // If add jobs, get objectIds from results
                        if (jobsItems.length) {
                            const resRBs = rbs.adds.length > 0 ? rbs.adds : rbs.updates;

                            //Get added RB
                            let rbObjectIds: string[] = resRBs.map((oneRes: any) => oneRes.objectId);
                            const where = `${ConfigService.getConfig().agol.operationalLayers.reportRb.fields.objectid} IN (${rbObjectIds.join(',')})`;
                            const filterdRbs: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportRb.index), where, false, '*')

                            filterdRbs.forEach((oneRes: IFeature, i: number) => {
                                if (jobsItems.length >= i) {
                                    const oneItem: ISocioEconomicImpactsTab = jobsItems[i];

                                    // If add mode
                                    if (resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs!.length >= i) {
                                        oneItem.seasonalCasualOccasional!.report_rb_id = oneRes.attributes.GlobalID;
                                        oneItem.shortTerm!.report_rb_id = oneRes.attributes.GlobalID;
                                        oneItem.longTerm!.report_rb_id = oneRes.attributes.GlobalID;
                                        oneItem.unspecified!.report_rb_id = oneRes.attributes.GlobalID;
                                    }

                                    jobs.push({ attributes: oneItem.seasonalCasualOccasional });
                                    jobs.push({ attributes: oneItem.shortTerm });
                                    jobs.push({ attributes: oneItem.longTerm });
                                    jobs.push({ attributes: oneItem.unspecified });
                                }
                            });

                            const rbJobs = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.index,
                                resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs?.length ? jobs : [],
                                resultsAndBenefits.updatesJobs && resultsAndBenefits.updatesJobs?.length ? jobs : [],
                                resultsAndBenefits.deletesJobs || [],
                            );
                        }
                    }

                }
            }

            return Promise.resolve(reportMain);

        } catch (err: any) {
            return Promise.reject(err);
        }
    }

    /**
     * Save Private Sector edits
     * @param generalInformation 
     * @param privateSectorSuccessFactors    
     * @returns 
     */
    public static async savePrivateSectorEdits(
        generalInformation: { adds?: any[], updates?: any[] } = {},
        resultsCountryIndicator: { addsCountryIndicator?: IReportCountriesIndicators[], updatesCountryIndicator?: IReportCountriesIndicators[], deletesCountryIndicator?: number[] } = {},
        privateSectorSuccessFactors: {
            policiesAndStrategies?: { adds?: any[], deletes?: any[] },
            planningMethodologies?: { adds?: any[], deletes?: any[] },
            financialFlows?: { adds?: any[], deletes?: any[] },
            monitoringSystems?: { adds?: any[], deletes?: any[] },
        } = {},
        resultsAndBenefits: { adds?: any[], updates?: any[], deletes?: any[], addsJobs?: IPrivateSectorSocioEconomicImpactsTab[], updatesJobs?: IPrivateSectorSocioEconomicImpactsTab[], deletesJobs?: number[], addsSplitUpValue?: IPrivateSectorSplitUpValues[], updatesSplitUpValue?: IPrivateSectorSplitUpValues[], deletesSplitUpValue?: number[] } = {}        
    ): Promise<any> {
        try {
            // 1) Report main
            const reportMain = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMain.index, [], generalInformation.updates);

            let reportMainObjectId = reportMain.adds && reportMain.adds?.length ? reportMain.adds[0].objectId :
                                        reportMain.updates && reportMain.updates?.length ? reportMain.updates[0].objectId : '';
                                        
            let reportMainGlobalId: any;
            const where = "OBJECTID='" + reportMainObjectId + "'";
            const filterdMain: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportMain.index), where, false, '*')
            reportMainGlobalId = filterdMain[0].attributes.GlobalID;

            //2) Country Indicators 
            if (resultsCountryIndicator) {
                const countryInd: IReportCountriesIndicators[] = resultsCountryIndicator.addsCountryIndicator && resultsCountryIndicator.addsCountryIndicator?.length ? resultsCountryIndicator.addsCountryIndicator :
                    resultsCountryIndicator.updatesCountryIndicator && resultsCountryIndicator.updatesCountryIndicator?.length ? resultsCountryIndicator.updatesCountryIndicator : [];
                const indicators: any[] = [];

                // If add jobs, get objectIds from results
                if (countryInd.length) {

                    if (resultsCountryIndicator.addsCountryIndicator!.length > 0) {
                        resultsCountryIndicator.addsCountryIndicator!.forEach(el => {
                            if (el.country_indicator != undefined && el.country_indicator != '') {
                                indicators.push({ attributes: el });
                            }
                        });
                    }

                    let reportId: any;
                    reportId = countryInd[0].report_id;
                    if (reportId != undefined && reportId != '') {
                        const where = "report_id='" + reportId + "'";
                        const filterdCountries: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index), where, false, '*')

                        if (filterdCountries !== undefined && filterdCountries.length > 0) {
                            const delCount: number[] = [];
                            filterdCountries.forEach((delRes: any, i: number) => {
                                delCount.push(delRes.attributes.OBJECTID);
                            });

                            resultsCountryIndicator.deletesCountryIndicator = delCount;
                            const rbSplitValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index,
                                [],
                                [],
                                resultsCountryIndicator.deletesCountryIndicator,
                            );

                            if (resultsCountryIndicator.addsCountryIndicator != null && resultsCountryIndicator.addsCountryIndicator!.length > 0) {
                                await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index,
                                    resultsCountryIndicator.addsCountryIndicator && resultsCountryIndicator.addsCountryIndicator?.length ? indicators : [],
                                    [],
                                    [],
                                );
                            }
                        }
                        else {
                            if (resultsCountryIndicator.addsCountryIndicator != null && resultsCountryIndicator.addsCountryIndicator!.length > 0) {
                                await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index,
                                    resultsCountryIndicator.addsCountryIndicator && resultsCountryIndicator.addsCountryIndicator?.length ? indicators : [],
                                    [],
                                    [],
                                );
                            }
                        }

                        // const rbindicators = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index,
                        //     resultsCountryIndicator.addsCountryIndicator && resultsCountryIndicator.addsCountryIndicator?.length ? indicators : [],
                        //     resultsCountryIndicator.updatesCountryIndicator && resultsCountryIndicator.updatesCountryIndicator?.length ? indicators : [],
                        //     resultsCountryIndicator.deletesCountryIndicator || [],
                        // );
                    }
                }
                else {
                    const rbindicators = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index,
                        [],
                        [],
                        resultsCountryIndicator.deletesCountryIndicator || [],
                    );
                }
            }

            // 3) Success factors
            if (privateSectorSuccessFactors) {                
                if (privateSectorSuccessFactors.policiesAndStrategies) {
                    const policyValueItems: ISuccessFactorsPolicyStrategies[] = privateSectorSuccessFactors.policiesAndStrategies && privateSectorSuccessFactors.policiesAndStrategies.adds ? privateSectorSuccessFactors.policiesAndStrategies.adds : [];

                    const policyValue: any[] = [];
                    let reportId: any;
                    if (policyValueItems.length) {
                        privateSectorSuccessFactors.policiesAndStrategies.adds?.forEach((oneRes: ISuccessFactorsPolicyStrategies, i: number) => {
                            if (policyValueItems.length >= i) {
                                const oneItem: ISuccessFactorsPolicyStrategies = policyValueItems[i];
                                // reportId = oneItem.report_id;
                                reportId = oneItem.report_id;


                                policyValue.push({ attributes: oneItem });
                            }
                        });

                        //  reportId = reportId == undefined ? policyValueItems[0].report_id : reportId;
                        let rbReportId: string[] = policyValueItems.map((oneRes: any) => oneRes.report_id);
                        reportId = (rbReportId[0] == undefined ? policyValueItems[0].report_id : rbReportId[0]);
                        if (reportId != undefined && reportId != '') {
                            const where = "report_id='" + reportId + "'";
                            const filterdPolicies: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index), where, false, '*')

                            if (filterdPolicies !== undefined && filterdPolicies.length > 0) {
                                const delCount: number[] = [];
                                filterdPolicies.forEach((delRes: any, i: number) => {
                                    delCount.push(delRes.attributes.OBJECTID);
                                });

                                privateSectorSuccessFactors.policiesAndStrategies.deletes = delCount;
                                const policiesValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.policiesAndStrategies.deletes,
                                );

                                if (privateSectorSuccessFactors.policiesAndStrategies.adds != null && privateSectorSuccessFactors.policiesAndStrategies.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                                        privateSectorSuccessFactors.policiesAndStrategies.adds && privateSectorSuccessFactors.policiesAndStrategies.adds?.length ? policyValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                            else {
                                if (privateSectorSuccessFactors.policiesAndStrategies.adds != null && privateSectorSuccessFactors.policiesAndStrategies.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                                        privateSectorSuccessFactors.policiesAndStrategies.adds && privateSectorSuccessFactors.policiesAndStrategies.adds?.length ? policyValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                        }
                    }
                    else {
                        if(reportMainGlobalId != undefined && reportMainGlobalId != null && reportMainGlobalId != '')
                        {
                            const countyList: string[] = [];
                            const where = "report_id='" + reportMainGlobalId + "'";
                            const filterdReportCountries: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index), where, false, '*')
                            if (filterdReportCountries !== undefined && filterdReportCountries.length > 0) {                                
                                filterdReportCountries.forEach((planRes: any, i: number) => {
                                    countyList.push(planRes.attributes.country_id);
                                });
                            }
                            //const wherePlan = "country_id IN ('" + countyList + "')";
                            const wherePolicy = "report_id='" + reportMainGlobalId + "' and country_id NOT IN ('" + countyList + "')";
                            const filterdPolicy: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index), wherePolicy, false, '*')
                            if (filterdPolicy !== undefined && filterdPolicy.length > 0) {
                                const delCount: number[] = [];
                                filterdPolicy.forEach((delRes: any, i: number) => {
                                    if(delRes.country_id != null || delRes.country_id != '')  
                                    {
                                        delCount.push(delRes.attributes.OBJECTID);
                                    }  
                                });

                                privateSectorSuccessFactors.policiesAndStrategies.deletes = delCount;
                                const policiesValuesDel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.policiesAndStrategies.deletes,
                                );
                            }
                        }
                        else
                        {
                            const policies = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                                [],
                                [],
                                privateSectorSuccessFactors.policiesAndStrategies && privateSectorSuccessFactors.policiesAndStrategies.deletes ? privateSectorSuccessFactors.policiesAndStrategies.deletes : []
                            );
                        }                        
                    }

                    // const policies = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportPolicies.index,
                    //     privateSectorSuccessFactors.policiesAndStrategies && privateSectorSuccessFactors.policiesAndStrategies.adds ? privateSectorSuccessFactors.policiesAndStrategies.adds : [],
                    //     [],
                    //     privateSectorSuccessFactors.policiesAndStrategies && privateSectorSuccessFactors.policiesAndStrategies.deletes ? privateSectorSuccessFactors.policiesAndStrategies.deletes : []
                    // );
                }
                // 2.2) Technical planning
                if (privateSectorSuccessFactors.planningMethodologies) {
                    const planningValueItems: IPrivateSectorSuccessFactorsPlanningMethodology[] = privateSectorSuccessFactors.planningMethodologies && privateSectorSuccessFactors.planningMethodologies.adds ? privateSectorSuccessFactors.planningMethodologies.adds : [];

                    const planningValue: any[] = [];
                    let reportId: any;
                    if (planningValueItems.length) {
                        privateSectorSuccessFactors.planningMethodologies.adds?.forEach((oneRes: IPrivateSectorSuccessFactorsPlanningMethodology, i: number) => {
                            if (planningValueItems.length >= i) {
                                const oneItem: IPrivateSectorSuccessFactorsPlanningMethodology = planningValueItems[i];
                                reportId = oneItem.report_id;


                                planningValue.push({ attributes: oneItem });
                            }
                        });

                        reportId = reportId == undefined ? planningValueItems[0].report_id : reportId;
                        if (reportId != undefined && reportId != '') {
                            const where = "report_id='" + reportId + "'";
                            const filterdPlanning: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index), where, false, '*')

                            if (filterdPlanning !== undefined && filterdPlanning.length > 0) {
                                const delCount: number[] = [];
                                filterdPlanning.forEach((delRes: any, i: number) => {
                                    delCount.push(delRes.attributes.OBJECTID);
                                });

                                privateSectorSuccessFactors.planningMethodologies.deletes = delCount;
                                const planningValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.planningMethodologies.deletes,
                                );

                                if (privateSectorSuccessFactors.planningMethodologies.adds != null && privateSectorSuccessFactors.planningMethodologies.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                                        privateSectorSuccessFactors.planningMethodologies.adds && privateSectorSuccessFactors.planningMethodologies.adds?.length ? planningValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                            else {
                                if (privateSectorSuccessFactors.planningMethodologies.adds != null && privateSectorSuccessFactors.planningMethodologies.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                                        privateSectorSuccessFactors.planningMethodologies.adds && privateSectorSuccessFactors.planningMethodologies.adds?.length ? planningValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                        }
                    }
                    else {
                        if(reportMainGlobalId != undefined && reportMainGlobalId != null && reportMainGlobalId != '')
                        {
                            const countyList: string[] = [];
                            const where = "report_id='" + reportMainGlobalId + "'";
                            const filterdReportCountries: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index), where, false, '*')
                            if (filterdReportCountries !== undefined && filterdReportCountries.length > 0) {                                
                                filterdReportCountries.forEach((planRes: any, i: number) => {
                                    countyList.push(planRes.attributes.country_id);
                                });
                            }
                            //const wherePlan = "country_id IN ('" + countyList + "')";
                            const wherePlan = "report_id='" + reportMainGlobalId + "' and country_id NOT IN ('" + countyList + "')";
                            const filterdPlan: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index), wherePlan, false, '*')
                            if (filterdPlan !== undefined && filterdPlan.length > 0) {
                                const delCount: number[] = [];
                                filterdPlan.forEach((delRes: any, i: number) => {
                                    if(delRes.country_id != null || delRes.country_id != '')  
                                    {
                                        delCount.push(delRes.attributes.OBJECTID);
                                    }  
                                });

                                privateSectorSuccessFactors.planningMethodologies.deletes = delCount;
                                const planningValuesDel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.planningMethodologies.deletes,
                                );
                            }
                        }
                        else
                        {
                            const methodologies = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                                [],
                                [],
                                privateSectorSuccessFactors.planningMethodologies && privateSectorSuccessFactors.planningMethodologies.deletes ? privateSectorSuccessFactors.planningMethodologies.deletes : []
                            );
                        }                        
                    }

                    // const methodologies = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.index,
                    //     privateSectorSuccessFactors.planningMethodologies && privateSectorSuccessFactors.planningMethodologies.adds ? privateSectorSuccessFactors.planningMethodologies.adds : [],
                    //     [],
                    //     privateSectorSuccessFactors.planningMethodologies && privateSectorSuccessFactors.planningMethodologies.deletes ? privateSectorSuccessFactors.planningMethodologies.deletes : []
                    // );
                }

                //2.3) Financial flows
                if (privateSectorSuccessFactors.financialFlows) {
                    const fundingValueItems: IPrivateSectorSuccessFactorsFinancialFlow[] = privateSectorSuccessFactors.financialFlows && privateSectorSuccessFactors.financialFlows.adds ? privateSectorSuccessFactors.financialFlows.adds : [];

                    const financialValue: any[] = [];
                    let reportId: any;
                    if (fundingValueItems.length) {
                        privateSectorSuccessFactors.financialFlows.adds?.forEach((oneRes: IPrivateSectorSuccessFactorsFinancialFlow, i: number) => {
                            if (fundingValueItems.length >= i) {
                                const oneItem: IPrivateSectorSuccessFactorsFinancialFlow = fundingValueItems[i];
                                reportId = oneItem.report_id;


                                financialValue.push({ attributes: oneItem });
                            }
                        });

                        reportId = reportId == undefined ? fundingValueItems[0].report_id : reportId;
                        if (reportId != undefined && reportId != '') {
                            const where = "report_id='" + reportId + "'";
                            const filterdPlanning: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index), where, false, '*')

                            if (filterdPlanning !== undefined && filterdPlanning.length > 0) {
                                const delCount: number[] = [];
                                filterdPlanning.forEach((delRes: any, i: number) => {
                                    delCount.push(delRes.attributes.OBJECTID);
                                });

                                privateSectorSuccessFactors.financialFlows.deletes = delCount;
                                const planningValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.financialFlows.deletes,
                                );

                                if (privateSectorSuccessFactors.financialFlows.adds != null && privateSectorSuccessFactors.financialFlows.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                                        privateSectorSuccessFactors.financialFlows.adds && privateSectorSuccessFactors.financialFlows.adds?.length ? financialValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                            else {
                                if (privateSectorSuccessFactors.financialFlows.adds != null && privateSectorSuccessFactors.financialFlows.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                                        privateSectorSuccessFactors.financialFlows.adds && privateSectorSuccessFactors.financialFlows.adds?.length ? financialValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                        }
                    }
                    else {
                        if(reportMainGlobalId != undefined && reportMainGlobalId != null && reportMainGlobalId != '')
                        {
                            const countyList: string[] = [];
                            const where = "report_id='" + reportMainGlobalId + "'";
                            const filterdReportCountries: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index), where, false, '*')
                            if (filterdReportCountries !== undefined && filterdReportCountries.length > 0) {                                
                                filterdReportCountries.forEach((planRes: any, i: number) => {
                                    countyList.push(planRes.attributes.country_id);
                                });
                            }
                            //const wherePlan = "country_id IN ('" + countyList + "')";
                            const whereFund = "report_id='" + reportMainGlobalId + "' and country_id NOT IN ('" + countyList + "')";
                            const filterdFund: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index), whereFund, false, '*')
                            if (filterdFund !== undefined && filterdFund.length > 0) {
                                const delCount: number[] = [];
                                filterdFund.forEach((delRes: any, i: number) => {
                                    if(delRes.country_id != null || delRes.country_id != '')
                                    {
                                        delCount.push(delRes.attributes.OBJECTID);
                                    }  
                                });

                                privateSectorSuccessFactors.financialFlows.deletes = delCount;
                                const financialFlowsValuesDel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.financialFlows.deletes,
                                );
                            }
                        }
                        else
                        {
                            const financialFlows = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                                [],
                                [],
                                privateSectorSuccessFactors.financialFlows && privateSectorSuccessFactors.financialFlows.deletes ? privateSectorSuccessFactors.financialFlows.deletes : []
                            );
                        }                        
                    }
                    // const financialFlows = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.index,
                    //     privateSectorSuccessFactors.financialFlows && privateSectorSuccessFactors.financialFlows.adds ? privateSectorSuccessFactors.financialFlows.adds : [],
                    //     privateSectorSuccessFactors.financialFlows && privateSectorSuccessFactors.financialFlows.updates ? privateSectorSuccessFactors.financialFlows.updates : [],
                    //     []
                    // );
                }

                //2.4) Monitoring Systems
                if (privateSectorSuccessFactors.monitoringSystems) {
                    const systemValueItems: IPrivateSectorSuccessFactorsMonitoringSystem[] = privateSectorSuccessFactors.monitoringSystems && privateSectorSuccessFactors.monitoringSystems.adds ? privateSectorSuccessFactors.monitoringSystems.adds : [];

                    const systemValue: any[] = [];
                    let reportId: any;
                    if (systemValueItems.length) {
                        privateSectorSuccessFactors.monitoringSystems.adds?.forEach((oneRes: IPrivateSectorSuccessFactorsMonitoringSystem, i: number) => {
                            if (systemValueItems.length >= i) {
                                const oneItem: IPrivateSectorSuccessFactorsMonitoringSystem = systemValueItems[i];
                                reportId = oneItem.report_id;

                                systemValue.push({ attributes: oneItem });
                            }
                        });

                        reportId = reportId == undefined ? systemValueItems[0].report_id : reportId;
                        if (reportId != undefined && reportId != '') {
                            const where = "report_id='" + reportId + "'";
                            const filterdSystems: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index), where, false, '*')

                            if (filterdSystems !== undefined && filterdSystems.length > 0) {
                                const delCount: number[] = [];
                                filterdSystems.forEach((delRes: any, i: number) => {
                                    delCount.push(delRes.attributes.OBJECTID);
                                });

                                privateSectorSuccessFactors.monitoringSystems.deletes = delCount;
                                const systemValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.monitoringSystems.deletes,
                                );

                                if (privateSectorSuccessFactors.monitoringSystems.adds != null && privateSectorSuccessFactors.monitoringSystems.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                                        privateSectorSuccessFactors.monitoringSystems.adds && privateSectorSuccessFactors.monitoringSystems.adds?.length ? systemValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                            else {
                                if (privateSectorSuccessFactors.monitoringSystems.adds != null && privateSectorSuccessFactors.monitoringSystems.adds!.length > 0) {
                                    await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                                        privateSectorSuccessFactors.monitoringSystems.adds && privateSectorSuccessFactors.monitoringSystems.adds?.length ? systemValue : [],
                                        [],
                                        [],
                                    );
                                }
                            }
                        }
                    }
                    else {
                        if(reportMainGlobalId != undefined && reportMainGlobalId != null && reportMainGlobalId != '')
                        {
                            const countyList: string[] = [];
                            const where = "report_id='" + reportMainGlobalId + "'";
                            const filterdReportCountries: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportCountriesIndicators.index), where, false, '*')
                            if (filterdReportCountries !== undefined && filterdReportCountries.length > 0) {                                
                                filterdReportCountries.forEach((planRes: any, i: number) => {
                                    countyList.push(planRes.attributes.country_id);
                                });
                            }
                            //const wherePlan = "country_id IN ('" + countyList + "')";
                            const whereSystem = "report_id='" + reportMainGlobalId + "' and country_id NOT IN ('" + countyList + "')";
                            const filterdSystem: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index), whereSystem, false, '*')
                            if (filterdSystem !== undefined && filterdSystem.length > 0) {
                                const delCount: number[] = [];
                                filterdSystem.forEach((delRes: any, i: number) => {
                                    if(delRes.country_id != null || delRes.country_id != '')  
                                    {
                                        delCount.push(delRes.attributes.OBJECTID);
                                    }  
                                });

                                privateSectorSuccessFactors.monitoringSystems.deletes = delCount;
                                const systemValuesDel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                                    [],
                                    [],
                                    privateSectorSuccessFactors.monitoringSystems.deletes,
                                );
                            }
                        }
                        else
                        {
                            const systems = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                                [],
                                [],
                                privateSectorSuccessFactors.monitoringSystems && privateSectorSuccessFactors.monitoringSystems.deletes ? privateSectorSuccessFactors.monitoringSystems.deletes : []
                            );
                        }                        
                    }

                    // const systems = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.index,
                    //     privateSectorSuccessFactors.monitoringSystems && privateSectorSuccessFactors.monitoringSystems.adds ? privateSectorSuccessFactors.monitoringSystems.adds : [],
                    //     [],
                    //     privateSectorSuccessFactors.monitoringSystems && privateSectorSuccessFactors.monitoringSystems.deletes ? privateSectorSuccessFactors.monitoringSystems.deletes : []
                    // );
                }
                
                //4) Results and benefits / Jobs                 
                if (resultsAndBenefits) {
                    let reportId: any;
                    const rbItems: IPrivateSectorResultsAndBenefitsItem[] = resultsAndBenefits.adds && resultsAndBenefits.adds?.length ? resultsAndBenefits.adds :
                    resultsAndBenefits.updates && resultsAndBenefits.updates?.length ? resultsAndBenefits.updates : [];

                    //const rbReport: any[] = [];
                    //rbReport.push({ attributes: resultsAndBenefits.adds });
                    const resRBs = resultsAndBenefits.adds;
                    let rbReportId: string[] = rbItems.map((oneRes: any) => oneRes.attributes.report_id);
                    reportId = (rbReportId == undefined ? rbItems[0].report_id : rbReportId[0]);

                    if (reportId != undefined && reportId != '') {
                        const where = "report_id='" + reportId + "'";
                        const filterdRBData: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportRb.index), where, false, '*')
                        const rbValue: any[] = [];

                        if (filterdRBData !== undefined && filterdRBData.length > 0) {
                            const delCount: number[] = [];
                            filterdRBData.forEach((delRes: any, i: number) => {
                                delCount.push(delRes.attributes.OBJECTID);
                            });

                            resultsAndBenefits.deletes = delCount;
                            const rbRBValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRb.index,
                                [],
                                [],
                                resultsAndBenefits.deletes,
                            );

                            if (resultsAndBenefits.adds != null && resultsAndBenefits.adds!.length > 0) {
                                const rbs = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRb.index,
                                    resultsAndBenefits.adds && resultsAndBenefits.adds?.length ? resRBs : [],
                                    [],
                                    [],
                                );
                            }
                        }
                        else {
                            if (resultsAndBenefits.adds != null && resultsAndBenefits.adds!.length > 0) {
                                await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRb.index,
                                    resultsAndBenefits.adds && resultsAndBenefits.adds?.length ? resRBs : [],
                                    [],
                                    [],
                                );
                            }
                        }
                    // 3.1) Results and benefits

                    // const rbs = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRb.index,
                    //     resultsAndBenefits.adds || [],
                    //     resultsAndBenefits.updates || [],
                    //     resultsAndBenefits.deletes || [],
                    // );

                    // 3.2) Jobs
                    if (resultsAndBenefits) {
                        const jobsItems: IPrivateSectorSocioEconomicImpactsTab[] = resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs?.length ? resultsAndBenefits.addsJobs :
                            resultsAndBenefits.updatesJobs && resultsAndBenefits.updatesJobs?.length ? resultsAndBenefits.updatesJobs : [];
                        
                        const jobs: any[] = [];
                        let reportId: any;

                        
                        // If add jobs, get objectIds from results
                        if (jobsItems.length) {
                            //const resRBs = rbs.adds.length > 0 ? rbs.adds : rbs.updates;

                            //Get added RB
                           // let rbObjectIds: string[] = rbItems.map((oneRes: any) => oneRes.attributes.report_id);
                            let rbReportId: string[] = rbItems.map((rboneRes: any) => rboneRes.attributes.report_id);
                            //const where = `${ConfigService.getConfig().agol.operationalLayers.reportRb.fields.objectid} IN (${rbObjectIds.join(',')})`;
                            reportId = (rbReportId == undefined ? '' : rbReportId[0]);
                            const where = "report_id='" + reportId + "'";
                            const filterdRbs: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportRb.index), where, false, '*')
 
                            filterdRbs.forEach((oneRes: IFeature, i: number) => {
                                if (jobsItems.length >= i) {
                                    const oneItem: IPrivateSectorSocioEconomicImpactsTab = jobsItems[i];
                                    if(oneItem != undefined)                                        
                                    {
                                    // If add mode
                                    if (resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs!.length >= i) {
                                       
                                        oneItem.psSeasonalCasualOccasional!.report_rb_id = oneRes.attributes.GlobalID;                                                                         
                                        oneItem.psLongTerm!.report_rb_id = oneRes.attributes.GlobalID;
                                        oneItem.psSeasonalCasualOccasional!.job_type = oneItem.job_type;
                                        oneItem.psLongTerm!.job_type = oneItem.job_type;
                                        reportId = oneRes.attributes.GlobalID;
                                    }
                                    
                                    if(oneItem.job_type == '1')
                                    {
                                        jobs.push({ attributes: oneItem.psSeasonalCasualOccasional });
                                    }
                                    else if(oneItem.job_type == '2')
                                    {
                                        jobs.push({ attributes: oneItem.psLongTerm });
                                    }
                                    else
                                    {
                                        jobs.push({ attributes: oneItem.psSeasonalCasualOccasional });
                                        jobs.push({ attributes: oneItem.psLongTerm });
                                    }
                                }
                                }
                            });

                            
                            if (reportId != undefined && reportId != '') {
                                const whereJob = "report_rb_id='" + reportId + "'";
                                const filterdJobs: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.index), whereJob, false, '*')

                                if (filterdJobs !== undefined && filterdJobs.length > 0) {
                                    const delCount: number[] = [];
                                    filterdJobs.forEach((delRes: any, i: number) => {
                                        delCount.push(delRes.attributes.OBJECTID);
                                    });

                                    resultsAndBenefits.deletesJobs = delCount;
                                    const rbRBJobValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.index,
                                        [],
                                        [],
                                        resultsAndBenefits.deletesJobs,
                                    );

                                    if (resultsAndBenefits.addsJobs != null && resultsAndBenefits.addsJobs!.length > 0) {
                                        const rbs = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.index,
                                            resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs?.length ? jobs : [],
                                            [],
                                            [],
                                        );
                                    }
                                }
                                else {
                                    if (resultsAndBenefits.addsJobs != null && resultsAndBenefits.addsJobs!.length > 0) {
                                        await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.index,
                                            resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs?.length ? jobs : [],
                                            [],
                                            [],
                                        );
                                    }
                                }
                            }

                            // const rbJobs = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.index,
                            //     resultsAndBenefits.addsJobs && resultsAndBenefits.addsJobs?.length ? jobs : [],
                            //     resultsAndBenefits.updatesJobs && resultsAndBenefits.updatesJobs?.length ? jobs : [],
                            //     resultsAndBenefits.deletesJobs || [],
                            // );
                        }
                    }

                    // 3.3) Split Value
                    if (resultsAndBenefits) {
                        const splitValueItems: IPrivateSectorSplitUpValues[] = resultsAndBenefits.addsSplitUpValue && resultsAndBenefits.addsSplitUpValue?.length ? resultsAndBenefits.addsSplitUpValue :
                            resultsAndBenefits.updatesSplitUpValue && resultsAndBenefits.updatesSplitUpValue?.length ? resultsAndBenefits.updatesSplitUpValue : [];

                        const splitValue: any[] = [];
                        // If add split value, get objectIds from results
                        // Cmd on 1may to ignore if condition on delete all record senario//
                        if (splitValueItems.length) {
                            //const resRBs = rbs.adds.length > 0 ? rbs.adds : rbs.updates;

                            //Get added RB
                            let reportId: any;
                            let rbReportId: string[] = rbItems.map((rboneRes: any) => rboneRes.attributes.report_id);
                            reportId = (rbReportId == undefined ? '' : rbReportId[0]);
                            const where = "report_id='" + reportId + "'";                           
                            const filterdRbs: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportRb.index), where, false, '*')
                            filterdRbs.forEach((oneRes: IFeature, j: number) => {                                    
                                    reportId = oneRes.attributes.GlobalID;
                                    let rbCountryId = oneRes.attributes.GID_0;

                                    resultsAndBenefits.addsSplitUpValue?.forEach((oneRes: IPrivateSectorSplitUpValues, i: number) => {
                                        if((oneRes.country_id != '' || oneRes.country_id != null) && oneRes.country_id == rbCountryId && splitValueItems.length >= i)
                                        {                                            
                                            const oneItem: IPrivateSectorSplitUpValues = splitValueItems[i];    
                                            oneItem.report_rb_id = reportId;    
                                            splitValue.push({ attributes: oneItem });                                            
                                        }
                                        else
                                        {
                                            if ((oneRes.country_id == '' || oneRes.country_id == null) && splitValueItems.length >= i) {
                                                const oneItem: IPrivateSectorSplitUpValues = splitValueItems[i];
        
                                                oneItem.report_rb_id = reportId;
        
                                                splitValue.push({ attributes: oneItem });
                                            } 
                                        }
                                    });                                
                            });

                                // resultsAndBenefits.addsSplitUpValue?.forEach((oneRes: IPrivateSectorSplitUpValues, i: number) => {
                                //     if (splitValueItems.length >= i) {
                                //         const oneItem: IPrivateSectorSplitUpValues = splitValueItems[i];

                                //         oneItem.report_rb_id = reportId;

                                //         splitValue.push({ attributes: oneItem });
                                //     }
                                // });

                                reportId = (reportId == undefined ? splitValueItems[0].report_rb_id : reportId);
                                if (reportId != undefined && reportId != '') {
                                    const whereSplit = "report_rb_id='" + reportId + "'";
                                    const filterdSplit: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportRbHaRestoSplit.index), whereSplit, false, '*')

                                    if (filterdSplit !== undefined && filterdSplit.length > 0) {
                                        const delCount: number[] = [];
                                        filterdSplit.forEach((delRes: any, i: number) => {
                                            delCount.push(delRes.attributes.OBJECTID);
                                        });

                                        resultsAndBenefits.deletesSplitUpValue = delCount;
                                        const rbSplitValuesdel = await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbHaRestoSplit.index,
                                            [],
                                            [],
                                            resultsAndBenefits.deletesSplitUpValue,
                                        );

                                        if (resultsAndBenefits.addsSplitUpValue != null && resultsAndBenefits.addsSplitUpValue!.length > 0) {
                                            await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbHaRestoSplit.index,
                                                resultsAndBenefits.addsSplitUpValue && resultsAndBenefits.addsSplitUpValue?.length ? splitValue : [],
                                                [],
                                                [],
                                            );
                                        }
                                    }
                                    else {
                                        if (resultsAndBenefits.addsSplitUpValue != null && resultsAndBenefits.addsSplitUpValue!.length > 0) {
                                            await QueryService.applyEdits(ConfigService.getConfig().agol.operationalLayers.reportRbHaRestoSplit.index,
                                                resultsAndBenefits.addsSplitUpValue && resultsAndBenefits.addsSplitUpValue?.length ? splitValue : [],
                                                [],
                                                [],
                                            );
                                        }
                                    }
                                }
                            //}
                        }
                    }
                }
                }               
            }

            return Promise.resolve(reportMain);

        } catch (err: any) {
            return Promise.reject(err);
        }
    }

    /**
    * Send submit mail
    * @returns 
    */
    public static async sendSubmitMail(to: string | undefined, country: string, year: string): Promise<void> {
        try {
            if (!to) {
                return Promise.resolve();
            }
            else {
                const res = await fetch(ConfigService.getConfig().submitMailUrl.replace('{to}', to).replace('{country}', country).replace('{year}', year));
                console.log(res);
                return Promise.resolve();
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    }
    public static async sendPSSubmitMail(to: string | undefined, company: string, year: string): Promise<void> {
        try {
            if (!to) {
                return Promise.resolve();
            }
            else {
                const res = await fetch(ConfigService.getConfig().submitPSMailUrl.replace('{to}', to).replace('{company}', company).replace('{year}', year));
                console.log(res);
                return Promise.resolve();
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    }
}